import React, { useEffect, useMemo, useState } from 'react';
import AutoAwesomeMosaicOutlinedIcon from '@mui/icons-material/AutoAwesomeMosaicOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import StackedBarChartOutlinedIcon from '@mui/icons-material/StackedBarChartOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ApiService } from '../../../services/api';
import { Store } from '../../../types';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  DailyReportAggregatePoint,
  DailyReportAggregateSnapshot,
  DailyReportMetric,
  DailyReportSectionSplit,
} from '../../types';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../../utils/safeStorage';

const CHAIN_STORAGE_KEY = 'forge_aggregate_report_chain_id';
const SUPPORTED_CHAIN_IDS = new Set<number>([46]);
const WINDOW_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
] as const;

type WindowDays = (typeof WINDOW_OPTIONS)[number]['days'];

type ChainOption = {
  chainId: number;
  chainName: string;
  timeZone: string;
};

const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)' };

function pad(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

function formatYmd(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function shiftDate(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function loadStoredChainId(): number | null {
  const stored = safeLocalStorageGetItem(CHAIN_STORAGE_KEY);
  if (!stored) return null;
  const parsed = Number(stored);
  return Number.isFinite(parsed) ? parsed : null;
}

function saveStoredChainId(chainId: number): void {
  safeLocalStorageSetItem(CHAIN_STORAGE_KEY, String(chainId));
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatTimestampUtc(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(value));
}

function splitRows(sectionSplit?: DailyReportSectionSplit | null): {
  key: string;
  label: string;
  metric: DailyReportMetric;
  color: string;
}[] {
  if (!sectionSplit) return [];
  return [
    {
      key: 'design-front',
      label: 'Design front',
      metric: sectionSplit.designFrontProductCrops,
      color: '#2E8BFF',
    },
    {
      key: 'design-non-front',
      label: 'Design non-front',
      metric: sectionSplit.designNonFrontProductCrops,
      color: '#8066FF',
    },
    {
      key: 'unassigned',
      label: 'Unassigned',
      metric: sectionSplit.unassignedProductCrops,
      color: '#D29922',
    },
    {
      key: 'carton',
      label: 'Carton',
      metric: sectionSplit.cartonCrops,
      color: '#1FBA8C',
    },
    {
      key: 'inactive',
      label: 'Inactive',
      metric: sectionSplit.inactiveCrops,
      color: '#8B949E',
    },
  ];
}

const controlStyle: React.CSSProperties = {
  appearance: 'none',
  WebkitAppearance: 'none',
  backgroundColor: 'var(--surface)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '0 34px 0 12px',
  height: 32,
  fontSize: 13,
  cursor: 'pointer',
  outline: 'none',
  minWidth: 180,
};

const cardStyle: React.CSSProperties = {
  padding: 18,
  minHeight: 118,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  background:
    'linear-gradient(180deg, color-mix(in srgb, var(--surface) 88%, var(--accent-subtle) 12%), var(--surface))',
};

const sectionGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 18,
};

const MetricCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
}> = ({ icon, label, value, detail }) => (
  <div className="forge-card" style={cardStyle}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div
        style={{
          color: 'var(--text-secondary)',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div style={{ color: 'var(--accent)' }}>{icon}</div>
    </div>
    <div style={{ marginTop: 14, fontSize: 30, fontWeight: 800, lineHeight: 1.05 }}>{value}</div>
    <div style={{ marginTop: 10, color: 'var(--text-secondary)', fontSize: 12 }}>{detail}</div>
  </div>
);

const LineChart: React.FC<{
  points: DailyReportAggregatePoint[];
}> = ({ points }) => {
  const availableValues = points
    .map((point) => point.totalDesigns)
    .filter((value): value is number => typeof value === 'number');
  const minValue = Math.min(...availableValues, 0);
  const maxValue = Math.max(...availableValues, 1);
  const width = 760;
  const height = 220;
  const padding = { top: 20, right: 20, bottom: 36, left: 56 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const xAt = (index: number) =>
    padding.left + (points.length <= 1 ? innerWidth / 2 : (index / (points.length - 1)) * innerWidth);
  const yAt = (value: number) =>
    padding.top + (1 - (value - minValue) / Math.max(maxValue - minValue, 1)) * innerHeight;

  const segments: string[] = [];
  let currentSegment: string[] = [];
  points.forEach((point, index) => {
    if (typeof point.totalDesigns !== 'number') {
      if (currentSegment.length > 0) {
        segments.push(currentSegment.join(' '));
        currentSegment = [];
      }
      return;
    }
    currentSegment.push(`${currentSegment.length === 0 ? 'M' : 'L'}${xAt(index)},${yAt(point.totalDesigns)}`);
  });
  if (currentSegment.length > 0) {
    segments.push(currentSegment.join(' '));
  }

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{ width: '100%', height: 240, display: 'block' }}
      role="img"
      aria-label="Total designs by day"
    >
      {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
        const value = minValue + (maxValue - minValue) * ratio;
        const y = yAt(value);
        return (
          <g key={ratio}>
            <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke="var(--border)" strokeDasharray={ratio === 0 ? undefined : '4 4'} />
            <text x={padding.left - 10} y={y + 4} fill="var(--text-secondary)" fontSize="11" textAnchor="end">
              {Math.round(value)}
            </text>
          </g>
        );
      })}
      <line x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} stroke="var(--border)" />
      <line x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} stroke="var(--border)" />
      {points.map((point, index) => (
        <text
          key={point.selectedDate}
          x={xAt(index)}
          y={height - 10}
          fill="var(--text-secondary)"
          fontSize="10"
          textAnchor="middle"
        >
          {point.selectedDate.slice(5)}
        </text>
      ))}
      {points.map((point, index) => {
        const x = xAt(index);
        if (typeof point.totalDesigns !== 'number') {
          return (
            <line
              key={point.selectedDate}
              x1={x}
              x2={x}
              y1={padding.top}
              y2={height - padding.bottom}
              stroke="var(--text-tertiary)"
              strokeDasharray="2 4"
              opacity="0.35"
            />
          );
        }
        return null;
      })}
      {segments.map((segment, index) => (
        <path key={index} d={segment} fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      ))}
      {points.map((point, index) => {
        if (typeof point.totalDesigns !== 'number') return null;
        return (
          <circle
            key={point.selectedDate}
            cx={xAt(index)}
            cy={yAt(point.totalDesigns)}
            r={3.5}
            fill="var(--accent)"
            stroke="var(--surface)"
            strokeWidth={2}
          />
        );
      })}
    </svg>
  );
};

const StackedBarChart: React.FC<{
  points: DailyReportAggregatePoint[];
}> = ({ points }) => {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.max(points.length, 1)}, minmax(12px, 1fr))`,
        gap: 8,
        alignItems: 'end',
        minHeight: 250,
      }}
      data-testid="aggregate-section-split-chart"
    >
      {points.map((point) => {
        const rows = splitRows(point.sectionSplit);
        const total = point.sectionSplit?.totalSectionedCrops ?? 0;
        return (
          <div key={point.selectedDate} style={{ display: 'grid', gap: 8 }}>
            <div
              title={
                point.snapshotAvailable
                  ? `${point.selectedDate} · ${formatNumber(total)} sectioned crops`
                  : `${point.selectedDate} · snapshot unavailable`
              }
              style={{
                height: 190,
                borderRadius: 10,
                border: '1px solid var(--border)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column-reverse',
                background: point.snapshotAvailable
                  ? 'var(--elevated)'
                  : 'repeating-linear-gradient(135deg, var(--elevated), var(--elevated) 8px, color-mix(in srgb, var(--border) 55%, transparent) 8px, color-mix(in srgb, var(--border) 55%, transparent) 12px)',
              }}
            >
              {point.snapshotAvailable
                ? rows.map((row) => (
                    <div
                      key={row.key}
                      style={{
                        height: `${row.metric.pct}%`,
                        minHeight: row.metric.count > 0 ? 6 : 0,
                        background: row.color,
                      }}
                    />
                  ))
                : null}
            </div>
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 10 }}>
              {point.selectedDate.slice(5)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const AggregatedDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast } = useNotificationContext();
  const [chainOptions, setChainOptions] = useState<ChainOption[]>([]);
  const [selectedChainId, setSelectedChainId] = useState<number | null>(loadStoredChainId);
  const [windowDays, setWindowDays] = useState<WindowDays>(30);
  const [snapshot, setSnapshot] = useState<DailyReportAggregateSnapshot | null>(null);
  const [isLoadingChains, setIsLoadingChains] = useState(true);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadChains = async () => {
      setIsLoadingChains(true);
      try {
        const stores = await ApiService.fetchStores(token);
        if (cancelled) return;
        const chainsById = stores.reduce<Map<number, ChainOption>>((acc, store: Store) => {
          if (!acc.has(store.chainId)) {
            acc.set(store.chainId, {
              chainId: store.chainId,
              chainName: store.chainLegibleName || store.chainName,
              timeZone: store.timeZone,
            });
          }
          return acc;
        }, new Map());
        const nextOptions = [...chainsById.values()]
          .filter((option) => SUPPORTED_CHAIN_IDS.has(option.chainId))
          .sort((a, b) => a.chainName.localeCompare(b.chainName));
        setChainOptions(nextOptions);

        const storedChainId = loadStoredChainId();
        const resolvedChainId =
          nextOptions.find((option) => option.chainId === storedChainId)?.chainId ?? nextOptions[0]?.chainId ?? null;
        setSelectedChainId(resolvedChainId);
        if (resolvedChainId !== null) {
          saveStoredChainId(resolvedChainId);
        }
      } catch (error) {
        if (!cancelled) {
          showErrorToast(error instanceof Error ? error.message : 'Error loading chains');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingChains(false);
        }
      }
    };

    loadChains();
    return () => {
      cancelled = true;
    };
  }, [showErrorToast, token]);

  useEffect(() => {
    if (!selectedChainId) {
      setSnapshot(null);
      return;
    }

    const today = new Date();
    const dateTo = formatYmd(today);
    const dateFrom = formatYmd(shiftDate(today, -(windowDays - 1)));

    let cancelled = false;

    const loadSnapshot = async () => {
      setIsLoadingSnapshot(true);
      try {
        const nextSnapshot = await ForgeApiService.fetchAggregateReport(
          token,
          selectedChainId,
          dateFrom,
          dateTo
        );
        if (!cancelled) {
          setSnapshot(nextSnapshot);
        }
      } catch (error) {
        if (!cancelled) {
          setSnapshot(null);
          showErrorToast(error instanceof Error ? error.message : 'Error loading aggregate report');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSnapshot(false);
        }
      }
    };

    loadSnapshot();
    return () => {
      cancelled = true;
    };
  }, [selectedChainId, showErrorToast, token, windowDays]);

  const latestAvailablePoint = useMemo(
    () => [...(snapshot?.points ?? [])].reverse().find((point) => point.snapshotAvailable) ?? null,
    [snapshot]
  );

  const latestSplitRows = splitRows(latestAvailablePoint?.sectionSplit);
  const selectedChain = chainOptions.find((option) => option.chainId === selectedChainId) ?? null;

  return (
    <div className="page-content">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 20,
        }}
      >
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <select
            value={selectedChainId ?? ''}
            onChange={(event) => {
              const nextChainId = Number(event.target.value);
              setSelectedChainId(nextChainId);
              saveStoredChainId(nextChainId);
            }}
            style={controlStyle}
          >
            {chainOptions.map((option) => (
              <option key={option.chainId} value={option.chainId}>
                {option.chainName}
              </option>
            ))}
          </select>
          <div
            style={{
              display: 'inline-flex',
              borderRadius: 6,
              overflow: 'hidden',
              border: '1px solid var(--border)',
            }}
          >
            {WINDOW_OPTIONS.map((option, index) => (
              <button
                key={option.days}
                data-testid={`aggregate-window-${option.label}`}
                onClick={() => setWindowDays(option.days)}
                style={{
                  padding: '6px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  background: windowDays === option.days ? 'var(--accent)' : 'var(--surface)',
                  color: windowDays === option.days ? '#fff' : 'var(--text-secondary)',
                  borderRight:
                    index === WINDOW_OPTIONS.length - 1 ? 'none' : '1px solid var(--border)',
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {snapshot && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
            {snapshot.summary.availableDays} available days · {snapshot.summary.missingDays} missing days
          </div>
        )}
      </div>

      {!isLoadingChains && !selectedChain && (
        <div className="forge-card" style={{ padding: 24 }}>
          <div className="forge-card-title">No Supported Chains</div>
          <div style={{ color: 'var(--text-secondary)' }}>
            No Forge-supported chains are available for the aggregate report yet.
          </div>
        </div>
      )}

      {isLoadingSnapshot && (
        <div className="forge-card" style={{ padding: 24, marginBottom: 20 }}>
          <div className="forge-card-title">Loading Aggregate Report</div>
          <div style={{ color: 'var(--text-secondary)' }}>
            Reading cached daily snapshots for the selected historical window.
          </div>
        </div>
      )}

      {snapshot && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 18,
              marginBottom: 20,
            }}
          >
            <MetricCard
              icon={<CalendarTodayOutlinedIcon />}
              label="Window"
              value={`${snapshot.summary.totalDays} days`}
              detail={`${snapshot.dateFrom} → ${snapshot.dateTo}`}
            />
            <MetricCard
              icon={<AutoAwesomeMosaicOutlinedIcon />}
              label="Latest Total Designs"
              value={formatNumber(latestAvailablePoint?.totalDesigns ?? 0)}
              detail={
                latestAvailablePoint?.computedAtUtc
                  ? `Last available snapshot: ${formatTimestampUtc(latestAvailablePoint.computedAtUtc)}`
                  : 'No available snapshot in this window'
              }
            />
            <MetricCard
              icon={<TimelineOutlinedIcon />}
              label="Available Days"
              value={formatNumber(snapshot.summary.availableDays)}
              detail={`${formatNumber(snapshot.summary.missingDays)} days missing cached snapshots`}
            />
            <MetricCard
              icon={<StackedBarChartOutlinedIcon />}
              label="Latest Sectioned Crops"
              value={formatNumber(latestAvailablePoint?.sectionSplit?.totalSectionedCrops ?? 0)}
              detail={
                latestAvailablePoint?.sectionSplit
                  ? 'Mutually exclusive split: design front/non-front, unassigned, carton, inactive'
                  : 'Older or missing snapshot has no section split payload'
              }
            />
          </div>

          <div style={{ ...sectionGridStyle, gridTemplateColumns: 'minmax(0, 1.2fr) minmax(320px, 0.8fr)', marginBottom: 20 }}>
            <div className="forge-card">
              <div className="forge-card-title">Total Designs by Day</div>
              <div style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>
                Historical design count from cached daily snapshots. Missing days are shown as gaps, not zeros.
              </div>
              <LineChart points={snapshot.points} />
            </div>

            <div className="forge-card">
              <div className="forge-card-title">Latest Section Split</div>
              <div style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>
                Current bucket mix for the most recent available day in this window.
              </div>
              {latestAvailablePoint?.sectionSplit ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  {latestSplitRows.map((row) => (
                    <div
                      key={row.key}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(120px, 150px) 1fr auto',
                        gap: 12,
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{row.label}</div>
                      <div style={{ height: 10, background: 'var(--elevated)', borderRadius: 999, overflow: 'hidden' }}>
                        <div
                          style={{
                            width: `${Math.max(row.metric.pct, row.metric.count > 0 ? 2 : 0)}%`,
                            height: '100%',
                            borderRadius: 999,
                            background: row.color,
                          }}
                        />
                      </div>
                      <div style={{ ...mono, color: 'var(--text-secondary)', fontSize: 12 }}>
                        {formatNumber(row.metric.count)} · {formatPercent(row.metric.pct)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--text-secondary)' }}>
                  No section split is available in the latest cached snapshot for this window.
                </div>
              )}
            </div>
          </div>

          <div className="forge-card">
            <div className="forge-card-title">Section Split by Day</div>
            <div style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>
              Each column is one cached day. Missing days remain visibly unavailable instead of being filled with zeros.
            </div>
            <StackedBarChart points={snapshot.points} />
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
              {latestSplitRows.map((row) => (
                <div
                  key={row.key}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    color: 'var(--text-secondary)',
                    fontSize: 12,
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: row.color,
                      display: 'inline-block',
                    }}
                  />
                  <span>{row.label}</span>
                </div>
              ))}
              <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                <span style={{ ...mono }}>striped</span> = missing snapshot
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
